import React from 'react';
import PropTypes from 'prop-types';
import { Spacing } from '@reservamos/elements';
import LoyaltyPassengerCard from 'ui/atoms/LoyaltyPassengerCard';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import PassengerBusCategories from './PassengerBusCategories';
import PassengerInsuranceCheckbox from '../../PassengerInsuranceCheckbox';
import usePurchase from '../../../hooks/store/usePurchase';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';

/**
 * DotersLoyaltyPassengerView component.
 * Renders the loyalty passenger card view for Doters wallet users.
 * @param {object} props - passenger, passengerFields, isFirstPassenger, busDisabled, initialValues.
 * @param {string} props.passenger - The Redux Form field name for the passenger.
 * @param {object} props.passengerFields - The passenger fields data.
 * @param {boolean} props.isFirstPassenger - Whether this is the first passenger.
 * @param {boolean} props.busDisabled - Whether the bus category field is disabled.
 * @param {object} props.initialValues - Initial values for the passenger.
 * @returns {JSX.Element} The DotersLoyaltyPassengerView component.
 */
const DotersLoyaltyPassengerView = ({
  passenger,
  passengerFields,
  isFirstPassenger,
  busDisabled,
  initialValues,
}) => {
  // Use existing custom hooks to get data from Redux
  const purchase = usePurchase();
  const features = useWhitelabelFeatures();
  const { doters } = useLoyaltyPrograms();
  const dotersUser = doters.profile;

  // Extract needed data from purchase
  const {
    busCategories,
    isFetchingDetails: updatingCategories,
    isExchange,
    openTicket: isOpenTicket,
    insuranceOutgoingUnitAmount,
    insuranceIncomingUnitAmount,
  } = purchase;

  // Calculate derived values
  const shouldHideInsuranceCheckbox =
    !features.PASSENGERS_INSURANCE_ENABLED ||
    !(insuranceIncomingUnitAmount > 0 || insuranceOutgoingUnitAmount > 0);

  // Get Doters user information
  const dotersUserName = dotersUser
    ? `${dotersUser.firstName || ''} ${dotersUser.lastName || ''}`.trim()
    : '';
  const dotersUserEmail = dotersUser?.email || '';

  return (
    <Spacing vertical size="S">
      <LoyaltyPassengerCard name={dotersUserName} email={dotersUserEmail}>
        <PassengerBusCategories
          name={`${passenger}.busCategory`}
          passengerCategory={passengerFields.category}
          isFirstPassenger={isFirstPassenger}
          busCategories={busCategories}
          isUpdating={updatingCategories}
          isDisabled={busDisabled}
          isExchange={isExchange}
          passengerBusCategory={passengerFields.busCategory}
          isOpenTicket={isOpenTicket}
          initialBusCategory={initialValues.busCategory}
        />
        {!shouldHideInsuranceCheckbox && (
          <PassengerInsuranceCheckbox
            passenger={passenger}
            insuranceOutgoingUnitAmount={insuranceOutgoingUnitAmount}
            insuranceIncomingUnitAmount={insuranceIncomingUnitAmount}
            disabled={false}
          />
        )}
      </LoyaltyPassengerCard>
    </Spacing>
  );
};

DotersLoyaltyPassengerView.propTypes = {
  passenger: PropTypes.string.isRequired,
  passengerFields: PropTypes.object.isRequired,
  isFirstPassenger: PropTypes.bool.isRequired,
  busDisabled: PropTypes.bool.isRequired,
  initialValues: PropTypes.object.isRequired,
};

export default DotersLoyaltyPassengerView;
